import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import * as style from 'src/app/CardFeedDetails/components/CardFeedCardsTable.style'
import SvgMore from 'src/components/Svgs/SvgMore'
import {
  CardFeedCard,
  isInviteSentCardFeedCard,
} from 'src/queries/cardFeeds/cardFeeds.models'

const MENU_WIDTH = 200

type ActionMenuItemKey = 'reinvite' | 'unassign'
type ActionMenuItemStyle =
  | typeof style.actionMenuItemBranded
  | typeof style.actionMenuItemSevere

interface ActionMenuItem {
  action: (card: CardFeedCard) => void
  itemStyle: ActionMenuItemStyle
  key: ActionMenuItemKey
  label: string
}

export interface CardActionsMenuProps {
  card: CardFeedCard
  onReinvite: (card: CardFeedCard) => void
  onUnassign: (card: CardFeedCard) => void
  translate: (str: string) => string
}

const CardActionsMenu: React.FC<CardActionsMenuProps> = ({
  card,
  onReinvite,
  onUnassign,
  translate,
}) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<
    Partial<Record<ActionMenuItemKey, HTMLButtonElement | null>>
  >({})
  const prevOpenRef = useRef(open)
  // Trigger rect captured on open; positions the portaled menu with
  // position: fixed so the DataGrid cell's overflow: hidden can't clip it
  // (same approach as AssignCardholderDropdown's portalToBody).
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null)
  const canReinvite = isInviteSentCardFeedCard(card)
  const canUnassign = card.status === 'PENDING' || card.status === 'FAILED'
  const actionItems = useMemo<ActionMenuItem[]>(
    () => [
      ...(canReinvite
        ? [
            {
              action: onReinvite,
              itemStyle: style.actionMenuItemBranded,
              key: 'reinvite' as const,
              label: 'Resend Invitation',
            },
          ]
        : []),
      ...(canUnassign
        ? [
            {
              action: onUnassign,
              itemStyle: style.actionMenuItemSevere,
              key: 'unassign' as const,
              label: 'Unassign Card Holder',
            },
          ]
        : []),
    ],
    [canReinvite, canUnassign, onReinvite, onUnassign],
  )
  const hasActions = actionItems.length > 0
  const menuHeightEstimate = actionItems.length === 2 ? 88 : 48
  const getMenuItems = useCallback(
    (): HTMLButtonElement[] =>
      actionItems
        .map(({ key }) => itemRefs.current[key])
        .filter((el): el is HTMLButtonElement => el != null),
    [actionItems],
  )

  // Return focus to the trigger when the menu closes (Escape, selection,
  // Tab) so keyboard users aren't dropped at the top of the document. Only
  // restore when focus actually fell to <body> (the focused menuitem
  // unmounted) — an outside click on another control keeps its focus.
  useEffect(() => {
    if (
      prevOpenRef.current &&
      !open &&
      document.activeElement === document.body
    ) {
      triggerRef.current?.focus()
    }
    prevOpenRef.current = open
  }, [open])

  // WAI-ARIA menu pattern: focus moves into the menu when it opens. If a
  // refetch changes the available actions while focus is already inside the
  // menu, preserve the user's current position unless that focused item
  // unmounted.
  useEffect(() => {
    if (!open) return
    const firstItem = getMenuItems()[0]
    const activeElement = document.activeElement
    if (
      !menuRef.current ||
      activeElement === document.body ||
      !menuRef.current.contains(activeElement)
    ) {
      firstItem?.focus()
    }
  }, [open, canReinvite, canUnassign, getMenuItems])

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    // The portaled menu is positioned with position: fixed from the trigger's
    // rect, so recompute it on scroll/resize to keep the menu glued to the
    // trigger. Capture phase catches scrolls inside the grid's scroller.
    const handleViewportChange = (): void => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect())
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('scroll', handleViewportChange, true)
    window.addEventListener('resize', handleViewportChange)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('scroll', handleViewportChange, true)
      window.removeEventListener('resize', handleViewportChange)
    }
  }, [open])

  const menuId = `card-feed-card-actions-menu-${card.id}`
  const triggerId = `${menuId}-trigger`

  // A refetch can flip the row's status while the menu is open (the cards
  // query is always-stale); close so aria-expanded doesn't stay true on a
  // disabled trigger and the open-only listeners tear down.
  useEffect(() => {
    if (open && !hasActions) {
      setOpen(false)
    }
  }, [open, hasActions])

  const handleTriggerClick = (): void => {
    if (!open && triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect())
    }
    setOpen((prev) => !prev)
  }

  const handleSelect = (action: (card: CardFeedCard) => void): void => {
    setOpen(false)
    action(card)
  }

  // Roving focus per the WAI-ARIA menu pattern: ArrowDown/ArrowUp cycle,
  // Home/End jump, Tab closes (focus returns to the trigger via the effect).
  const handleMenuKeyDown = (e: React.KeyboardEvent): void => {
    const items = getMenuItems()
    if (items.length === 0) return
    const activeIndex = items.findIndex((el) => el === document.activeElement)
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        items[(activeIndex + 1) % items.length].focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        items[(activeIndex - 1 + items.length) % items.length].focus()
        break
      case 'Home':
        e.preventDefault()
        items[0].focus()
        break
      case 'End':
        e.preventDefault()
        items[items.length - 1].focus()
        break
      case 'Tab':
        e.preventDefault()
        setOpen(false)
        break
      default:
        break
    }
  }

  const menu =
    open && hasActions && triggerRect ? (
      <div
        ref={menuRef}
        role="menu"
        id={menuId}
        tabIndex={-1}
        aria-labelledby={triggerId}
        css={style.actionMenu}
        style={{
          position: 'fixed',
          // Right-align to the trigger, clamped so a narrow viewport can't
          // push the menu off the left edge.
          left: Math.max(8, triggerRect.right - MENU_WIDTH),
          width: MENU_WIDTH,
          zIndex: 1000,
          // Open downward unless the menu would run past the viewport.
          ...(window.innerHeight - triggerRect.bottom >= menuHeightEstimate
            ? { top: triggerRect.bottom + 2 }
            : { bottom: window.innerHeight - triggerRect.top + 2 }),
        }}
        data-testid="card-feed-card-actions-menu"
        onKeyDown={handleMenuKeyDown}
      >
        {actionItems.map(({ action, itemStyle, key, label }) => (
          <button
            key={key}
            ref={(el): void => {
              itemRefs.current[key] = el
            }}
            type="button"
            role="menuitem"
            tabIndex={-1}
            css={[style.actionMenuItem, itemStyle]}
            onClick={(): void => handleSelect(action)}
          >
            {translate(label)}
          </button>
        ))}
      </div>
    ) : null

  // Only rows with valid actions enable the trigger; it stays visible but
  // disabled for ASSIGNED/UNASSIGNED rows so the column reads consistently.
  return (
    <div>
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        css={style.actionTrigger}
        data-testid="card-feed-card-actions"
        aria-label={translate('Row actions')}
        aria-haspopup="menu"
        aria-expanded={open}
        // Only reference the menu while it's actually rendered (it portals in
        // on open); a dangling aria-controls id is invalid for assistive tech.
        aria-controls={open ? menuId : undefined}
        disabled={!hasActions}
        onClick={handleTriggerClick}
      >
        <SvgMore width={18} height={18} viewBox="0 0 24 24" />
      </button>
      {menu && ReactDOM.createPortal(menu, document.body)}
    </div>
  )
}

export default CardActionsMenu
